import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  DailyQuestAssignAndGetResponse,
} from '../../models/apiTypes';
import { useCreateUserAction } from '../../hooks/useCreateUserAction';
import { useNavigate } from 'react-router-dom';
import ModalFrame from '../primitives/ModalFrame';
import { useAdQuestProgress } from '../../hooks/useAdQuestProgress';
import { useAdQuestHandler, AD_QUEST_BLOCK_ID } from '../../hooks/useAdQuestHandler';
import AdsgramTaskQuest from '../primitives/AdsgramTaskQuest';
import { taskAdLog } from '../../utils/quest/taskAdDebugLog';
import { SHOW_TASK_AD_QUEST_41 } from '../dailyQuests/constants';
import { getQuestCategory } from '../dailyQuests/questCategories';
import { calculateTimeUntilAvailable } from '../dailyQuests/time';
import type { QuestCategory } from '../dailyQuests/types';
import { useClaimedRewardSync } from '../dailyQuests/hooks/useClaimedRewardSync';
import { useDailyQuestCategories } from '../dailyQuests/hooks/useDailyQuestCategories';
import { useDailyQuestList } from '../dailyQuests/hooks/useDailyQuestList';
import { useDailyQuestUiState } from '../dailyQuests/hooks/useDailyQuestUiState';
import { useDailyQuestsRefetch } from '../dailyQuests/hooks/useDailyQuestsRefetch';
import { useQuestActions } from '../dailyQuests/hooks/useQuestActions';
import { useQuestRewardClaiming } from '../dailyQuests/hooks/useQuestRewardClaiming';
import { useQuestRewardSounds } from '../dailyQuests/hooks/useQuestRewardSounds';
import { useXQuestFlow } from '../dailyQuests/hooks/useXQuestFlow';
import { useMarkQuestsOpened } from '../dailyQuests/hooks/useMarkQuestsOpened';
import DailyQuestCard from '../dailyQuests/cards/DailyQuestCard';
import DailyQuestAuxiliaryModals from '../dailyQuests/cards/DailyQuestAuxiliaryModals';
import { cn } from '../cn';


interface DailyQuestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When true, renders as inline full-page content (no ModalFrame wrapper). */
  asScreen?: boolean;
  /** Controlled active category for the screen mode. */
  defaultCategory?: QuestCategory;
}

export default function DailyQuestsModal({ isOpen, onClose, asScreen, defaultCategory }: DailyQuestsModalProps) {
  const { createUserAction } = useCreateUserAction();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeCategory, setActiveCategory] = useState<QuestCategory>(defaultCategory ?? "daily");

  const { playExpGift, playCurrencyGift } = useQuestRewardSounds();
  const {
    questRunState: {
      claimingQuestIds,
      setClaimingQuestIds,
      showingAdQuestIds,
      setShowingAdQuestIds,
      claimedQuestIds,
      setClaimedQuestIds,
      startedVerificationQuestIds,
      setStartedVerificationQuestIds,
      isVerifyingQuest,
      setIsVerifyingQuest,
    },
    xTimestamps: {
      xQuestModerationTimestamps,
      setXQuestModerationTimestamps,
      xQuestVerificationStartTimestamps,
      setXQuestVerificationStartTimestamps,
      xQuestErrorTimestamps,
      setXQuestErrorTimestamps,
    },
    xModals: {
      showXVerificationLoadingModal,
      showXVerificationErrorModal,
      setShowXVerificationErrorModal,
      showXVerificationSuccessModal,
      setShowXVerificationSuccessModal,
      showModerationInfoModal,
      setShowModerationInfoModal,
      activeXModalQuestId,
      setActiveXModalQuestId,
    },
    questList: {
      sortedQuests,
      setSortedQuests,
    },
    taskAd: {
      taskAdAvailable,
      setTaskAdAvailable,
    },
    navigationConfirm: {
      navigationConfirmModal,
      setNavigationConfirmModal,
    },
    vipInfo: {
      showVipInfoModal,
      setShowVipInfoModal,
    },
  } = useDailyQuestUiState();
  
  // Хуки для обробки реклами та progress bar
  const { adQuestProgress, watchedAdQuestIds } = useAdQuestProgress(isOpen);
  useMarkQuestsOpened(isOpen);
  const { showAdFromAdsGram, closeAd } = useAdQuestHandler(AD_QUEST_BLOCK_ID);
  
  // Зберігаємо userQuestId квестів, які були оптимістично завершені (щоб refetch не перезаписував їх)
  const optimisticallyCompletedAdQuestsRef = useRef<Set<number>>(new Set());
  // Зберігаємо userQuestId квестів, нагороди яких були оптимістично забрані (щоб refetch не перезаписував їх)
  const optimisticallyClaimedRewardsRef = useRef<Set<number>>(new Set());

  const isAdShowing = showingAdQuestIds.size > 0;
  const handleBack = () => {
    if (isAdShowing) {
      const closed = closeAd();
      if (closed) {
        // Очищаємо стан показу реклами, щоб можна було знову відкрити
        setShowingAdQuestIds(new Set());
      }
      return;
    }
    onClose();
  };

  // Отримуємо дані з кешу напряму - вони завжди є, бо отримуються при підключенні до гри
  // Не виконуємо запити при відкритті попапу - просто використовуємо кеш
  const hourlyQuestsData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>(['hourlyQuests']);
  const marketingQuestsData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>(['marketingQuests']);
  const dailyQuestsData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>(['dailyQuests']);
  const mainQuestsData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>(['mainQuests']);
  const repeatableQuestsData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>(['repeatableQuests']);

  const {
    setCloudStorageItem,
    isXFollowQuest,
    getXUrlForQuest,
    getXQuestModerationTimestamp,
    clearXQuestModerationTimestamp,
    getXQuestVerificationStartTimestamp,
    clearXQuestVerificationStartTimestamp,
    isXQuestOnModeration,
    handleSubscribeToX,
    handleVerifyXSubscription,
  } = useXQuestFlow({
    isOpen,
    dailyQuestsData,
    hourlyQuestsData,
    marketingQuestsData,
    setStartedVerificationQuestIds,
    xQuestModerationTimestamps,
    setXQuestModerationTimestamps,
    xQuestVerificationStartTimestamps,
    setXQuestVerificationStartTimestamps,
    xQuestErrorTimestamps,
    setXQuestErrorTimestamps,
    setActiveXModalQuestId,
    setShowXVerificationErrorModal,
    setShowModerationInfoModal,
    createUserAction,
  });

  const {
    questsWithoutSorting,
    questsWithoutSortingRef,
    quest41State,
    quests,
  } = useDailyQuestList({
    hourlyQuestsData,
    marketingQuestsData,
    dailyQuestsData,
    mainQuestsData,
    repeatableQuestsData,
    t,
    i18nLanguage: i18n.language,
    claimedQuestIds,
    watchedAdQuestIds,
    adQuestProgress,
    isOpen,
    sortedQuests,
    setSortedQuests,
  });

  // TEMPORARY (see taskAdDebugLog.ts): this is the exact variable whose CSS
  // 'hidden' class made the quest-41 card disappear invisibly (visuallyFinished
  // starting true on a fresh mount short-circuited the effect that sets
  // taskAdAvailable — see AdsgramTaskQuest.tsx). Logging it directly, on every
  // render, is what would have caught that bug from the trace alone.
  taskAdLog('modalRenderTaskAdState', {
    quest41Present: quest41State != null,
    quest41UserQuestId: quest41State?.userQuestId ?? null,
    quest41IsFinished: quest41State?.isFinished ?? null,
    quest41IsRewardClaimed: quest41State?.isRewardClaimed ?? null,
    taskAdAvailable,
  });

  const { categoriesWithQuests } = useDailyQuestCategories({
    quests,
    activeCategory,
    setActiveCategory,
  });

  const { refetchAllQuests } = useDailyQuestsRefetch({
    queryClient,
    isOpen,
    hourlyQuestsData,
    marketingQuestsData,
    dailyQuestsData,
    mainQuestsData,
    repeatableQuestsData,
    optimisticallyCompletedAdQuestsRef,
    optimisticallyClaimedRewardsRef,
  });


  // Дані завжди є в кеші, тому не показуємо "Loading"
  const isLoadingQuests = false;

  const {
    needsStartButton,
    handleStartAction,
    handleTaskAdReward,
  } = useQuestActions({
    queryClient,
    optimisticallyCompletedAdQuestsRef,
    refetchAllQuests,
    showAdFromAdsGram,
    setShowingAdQuestIds,
    createUserAction,
    onClose,
    navigate,
    setStartedVerificationQuestIds,
    setCloudStorageItem,
    questsWithoutSortingRef,
  });

  const { handleClaimReward } = useQuestRewardClaiming({
    claimingQuestIds,
    setClaimedQuestIds,
    optimisticallyClaimedRewardsRef,
    setClaimingQuestIds,
    questsWithoutSorting,
    sortedQuests,
    setSortedQuests,
    t,
    queryClient,
    playExpGift,
    playCurrencyGift,
  });

  useClaimedRewardSync({
    hourlyQuestsData,
    marketingQuestsData,
    dailyQuestsData,
    isOpen,
    asScreen,
    setClaimedQuestIds,
  });


  if (!isOpen && !asScreen) {
    return null;
  }

  if (isLoadingQuests) {
    if (asScreen) {
      return <div className="p-[40px] text-center text-[18px] text-white">{t("Loading...")}</div>;
    }
    return (
      <ModalFrame
        isOpen={isOpen}
        onClose={onClose}
        variant="dialog"
        showBackButton={true}
        title={t("Quests")}
        headerClassName="mb-sm"
        backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:520px]"
      >
        <div className="text-[18px] text-white">{t("Loading...")}</div>
      </ModalFrame>
    );
  }

  const categoryPills = ([
    { id: "daily",    label: t("Daily") },
    { id: "partner",  label: t("Partner") + " 🔥" },
    { id: "mayor",    label: t("Mayor") },
    { id: "social",   label: t("Social") },
  ] as { id: QuestCategory; label: string }[]).filter(({ id }) => categoriesWithQuests.has(id));

  if (asScreen) {
    return (
      <>
        <div className="flex min-h-[calc(100vh-120px)] flex-col gap-lg px-xl pb-xl pt-0">
          {/* Category pills */}
          <div className="flex shrink-0 gap-md overflow-x-auto pb-xs">
            {categoryPills.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveCategory(id)}
                className={cn(
                  'shrink-0 cursor-pointer whitespace-nowrap rounded-xl px-[14px] py-sm text-[13px]',
                  activeCategory === id
                    ? 'border-none bg-[image:var(--gradient-brand-deep-angled)] font-bold text-white'
                    : 'border-[1.5px] border-white-alpha-20 bg-transparent font-normal text-white-alpha-55',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {quests.length === 0 ? (
            <div className="py-[40px] text-center text-white">{t("Quests are loading...")}</div>
          ) : (
            <>
            {activeCategory === "daily" && SHOW_TASK_AD_QUEST_41 && quest41State && (
              <div className={taskAdAvailable ? undefined : 'hidden'}>
                <AdsgramTaskQuest
                  rewardAmount={10000}
                  secondaryRewardAmount={5000}
                  onReward={handleTaskAdReward}
                  onClaim={() => quest41State?.userQuestId && handleClaimReward(quest41State.userQuestId)}
                  isFinished={quest41State?.isFinished ?? false}
                  isRewardClaimed={quest41State?.isRewardClaimed ?? false}
                  hasActiveProtection={quest41State?.hasActiveProtection ?? false}
                  onUnavailable={() => { taskAdLog('modalSetsTaskAdAvailable', { value: false }); setTaskAdAvailable(false); }}
                  onAvailable={() => { taskAdLog('modalSetsTaskAdAvailable', { value: true }); setTaskAdAvailable(true); }}
                />
              </div>
            )}
            {quests
              .filter(q => getQuestCategory(q) === activeCategory && q.questId !== 41)
              .map((quest) => {
                const shouldBeGrayed = quest.isFinished && quest.isRewardClaimed && !quest.isRepeatable;
                return (
                  <article
                    key={quest.questId}
                    className={cn(
                      'flex flex-col gap-lg rounded-sm px-2xl py-lg text-white',
                      shouldBeGrayed
                        ? 'bg-[image:var(--gradient-disabled-progress)] opacity-60'
                        : 'bg-[image:var(--gradient-brand-deep)]',
                    )}
                  >
                    <p className="m-0 [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal">
                      {quest.title}
                    </p>
                    <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal opacity-85">
                      {quest.description}
                    </p>
                  </article>
                );
              })
            }
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      title={t("Quests")}
      showBackButton={true}
      onBack={handleBack}
      variant="dialog"
      className="rounded-lg"
      headerClassName="mb-sm"
      backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:520px]"
    >
        <div className="flex flex-col gap-lg pb-md">
          {/* Category pills */}
          <div className="flex shrink-0 gap-md overflow-x-auto pb-xs">
            {categoryPills.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveCategory(id)}
                className={cn(
                  'shrink-0 cursor-pointer whitespace-nowrap rounded-xl px-[14px] py-sm text-[13px]',
                  activeCategory === id
                    ? 'border-none bg-[image:var(--gradient-brand-deep-angled)] font-bold text-white'
                    : 'border-[1.5px] border-white-alpha-20 bg-transparent font-normal text-white-alpha-55',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {quests.length === 0 ? (
            <div className="py-[40px] text-center text-white">
              {t("Quests are loading...")}
            </div>
          ) : (
            <>
            {activeCategory === "daily" && SHOW_TASK_AD_QUEST_41 && quest41State && (
              <div className={taskAdAvailable ? undefined : 'hidden'}>
                <AdsgramTaskQuest
                  rewardAmount={10000}
                  secondaryRewardAmount={5000}
                  onReward={handleTaskAdReward}
                  onClaim={() => quest41State?.userQuestId && handleClaimReward(quest41State.userQuestId)}
                  isFinished={quest41State?.isFinished ?? false}
                  isRewardClaimed={quest41State?.isRewardClaimed ?? false}
                  hasActiveProtection={quest41State?.hasActiveProtection ?? false}
                  onUnavailable={() => { taskAdLog('modalSetsTaskAdAvailable', { value: false }); setTaskAdAvailable(false); }}
                  onAvailable={() => { taskAdLog('modalSetsTaskAdAvailable', { value: true }); setTaskAdAvailable(true); }}
                />
              </div>
            )}
            {quests
              .filter(quest => getQuestCategory(quest) === activeCategory && quest.questId !== 41)
              .map((quest) => (
                <DailyQuestCard
                  key={quest.questId}
                  quest={quest}
                  t={t}
                  queryClient={queryClient}
                  navigate={navigate}
                  onClose={onClose}
                  isXFollowQuest={isXFollowQuest}
                  getXUrlForQuest={getXUrlForQuest}
                  calculateTimeUntilAvailable={calculateTimeUntilAvailable}
                  setShowVipInfoModal={setShowVipInfoModal}
                  isXQuestOnModeration={isXQuestOnModeration}
                  getXQuestModerationTimestamp={getXQuestModerationTimestamp}
                  getXQuestVerificationStartTimestamp={getXQuestVerificationStartTimestamp}
                  clearXQuestModerationTimestamp={clearXQuestModerationTimestamp}
                  clearXQuestVerificationStartTimestamp={clearXQuestVerificationStartTimestamp}
                  setActiveXModalQuestId={setActiveXModalQuestId}
                  setShowModerationInfoModal={setShowModerationInfoModal}
                  handleSubscribeToX={handleSubscribeToX}
                  handleVerifyXSubscription={handleVerifyXSubscription}
                  startedVerificationQuestIds={startedVerificationQuestIds}
                  isVerifyingQuest={isVerifyingQuest}
                  setIsVerifyingQuest={setIsVerifyingQuest}
                  setStartedVerificationQuestIds={setStartedVerificationQuestIds}
                  setCloudStorageItem={setCloudStorageItem}
                  handleStartAction={handleStartAction}
                  showingAdQuestIds={showingAdQuestIds}
                  setNavigationConfirmModal={setNavigationConfirmModal}
                  needsStartButton={needsStartButton}
                  handleClaimReward={handleClaimReward}
                  claimingQuestIds={claimingQuestIds}
                />
              ))
          }
          </>
          )}
        </div>
    </ModalFrame>

    <DailyQuestAuxiliaryModals
      showXVerificationLoadingModal={showXVerificationLoadingModal}
      activeXModalQuestId={activeXModalQuestId}
      getXUrlForQuest={getXUrlForQuest}
      showXVerificationErrorModal={showXVerificationErrorModal}
      setShowXVerificationErrorModal={setShowXVerificationErrorModal}
      showXVerificationSuccessModal={showXVerificationSuccessModal}
      setShowXVerificationSuccessModal={setShowXVerificationSuccessModal}
      showModerationInfoModal={showModerationInfoModal}
      setShowModerationInfoModal={setShowModerationInfoModal}
      showVipInfoModal={showVipInfoModal}
      setShowVipInfoModal={setShowVipInfoModal}
      navigationConfirmModal={navigationConfirmModal}
      setNavigationConfirmModal={setNavigationConfirmModal}
      onClose={onClose}
      navigate={navigate}
      t={t}
    />
  </>
  );
}
